import DeleteRounded from "@mui/icons-material/DeleteRounded";
import EditRounded from "@mui/icons-material/EditRounded";
import RepeatRounded from "@mui/icons-material/RepeatRounded";
import {
    alpha,
    ButtonBase,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useState } from "react";
import DeleteSlotModal from "../../Modals/DeleteSlotModal";
import MakeRecurringSlotModal from "../../Modals/MakeRecurringSlotModal";
import BlockTimeModal from "./BlockTimeModal";
import { FONTS, CARD_STYLES } from "../../../constants/styles";

dayjs.extend(utc);

export interface BlockedTimeSlot {
    key: string;
    start: dayjs.Dayjs;
    end: dayjs.Dayjs;
    repeats: "none" | "weekly" | "monthly";
    note: string;
}

interface Props {
    slot: BlockedTimeSlot;
    onUnblockSlot: (slot: BlockedTimeSlot, mode: "single" | "all") => void;
    onRequestMakeRecurring: (
        slot: BlockedTimeSlot,
        interval: "weekly" | "monthly"
    ) => void;
    onEditSlot?: (
        slot: BlockedTimeSlot,
        start: dayjs.Dayjs,
        end: dayjs.Dayjs,
        note?: string
    ) => void;
}

const BlockedSlotItem = ({
    slot,
    onUnblockSlot,
    onRequestMakeRecurring,
    onEditSlot,
}: Props) => {
    const theme = useTheme();
    const hex = theme.palette.primary.main;
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [makeRecurringOpen, setMakeRecurringOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);

    const isRecurring = slot.repeats !== "none";
    const isPast = slot.start.isBefore(dayjs());
    const canEdit = !!onEditSlot && !isRecurring && !isPast;

    return (
        <>
            <Stack
                direction="row"
                width="100%"
                alignItems="stretch"
                spacing={1.5}
            >
                {/* Time column */}
                <Stack
                    direction="column"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ flex: "0 0 auto", width: 44, pt: 1.5, pb: 1.5 }}
                >
                    <Typography
                        fontFamily={FONTS.display}
                        fontWeight={600}
                        fontSize="13px"
                        color="text.secondary"
                    >
                        {slot.start.format("HH:mm")}
                    </Typography>
                    <Typography
                        fontFamily={FONTS.display}
                        fontWeight={600}
                        fontSize="13px"
                        color="text.secondary"
                    >
                        {slot.end.format("HH:mm")}
                    </Typography>
                </Stack>

                {/* Card with left accent bar */}
                <Stack
                    direction="column"
                    justifyContent="flex-start"
                    alignItems="flex-start"
                    flex={1}
                    sx={{
                        ...CARD_STYLES,
                        p: 1.5,
                        pl: 2.5,
                        bgcolor: alpha(hex, 0.04),
                        position: "relative",
                        overflow: "hidden",
                        "&::before": {
                            content: '""',
                            position: "absolute",
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: "4px",
                            bgcolor: alpha(hex, 0.3),
                            borderRadius: "4px 0 0 4px",
                        },
                    }}
                    spacing={1.5}
                >
                    <Typography
                        fontFamily={FONTS.display}
                        fontWeight={600}
                        fontSize="15px"
                        color="text.primary"
                    >
                        {slot.note ?? "Personal Time"}
                    </Typography>

                    {/* Inline icon-only actions: Edit · Repeat · Delete.
                        Icon-only avoids locale overflow (RU labels like
                        "Редактировать" don't fit on mobile widths). */}
                    <Stack direction="row" spacing={1}>
                        {canEdit && (
                            <ButtonBase
                                onClick={() => setEditOpen(true)}
                                aria-label="Edit"
                                sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: "50%",
                                    bgcolor: alpha(hex, 0.08),
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <EditRounded
                                    sx={{ fontSize: 18, color: hex }}
                                />
                            </ButtonBase>
                        )}

                        <ButtonBase
                            onClick={() => setMakeRecurringOpen(true)}
                            aria-label="Repeat"
                            sx={{
                                width: 32,
                                height: 32,
                                borderRadius: "50%",
                                bgcolor: alpha(hex, 0.08),
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <RepeatRounded
                                sx={{ fontSize: 18, color: hex }}
                            />
                        </ButtonBase>

                        <ButtonBase
                            onClick={() => setConfirmDeleteOpen(true)}
                            aria-label="Remove"
                            sx={{
                                width: 32,
                                height: 32,
                                borderRadius: "50%",
                                bgcolor: alpha(theme.palette.error.main, 0.08),
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <DeleteRounded
                                sx={{
                                    fontSize: 18,
                                    color: theme.palette.error.main,
                                }}
                            />
                        </ButtonBase>
                    </Stack>
                </Stack>
            </Stack>

            <DeleteSlotModal
                open={confirmDeleteOpen}
                slot={slot}
                onClose={() => setConfirmDeleteOpen(false)}
                onConfirm={(mode) => onUnblockSlot(slot, mode)}
            />

            <MakeRecurringSlotModal
                open={makeRecurringOpen}
                slot={slot}
                onClose={() => setMakeRecurringOpen(false)}
                onConfirm={(interval) => onRequestMakeRecurring(slot, interval)}
            />

            {canEdit && (
                <BlockTimeModal
                    open={editOpen}
                    onClose={() => setEditOpen(false)}
                    onConfirm={(start, end, note) =>
                        onEditSlot!(slot, start, end, note)
                    }
                    start={slot.start}
                    end={slot.end}
                    mode="edit"
                    initialNote={slot.note ?? ""}
                />
            )}
        </>
    );
};

export default BlockedSlotItem;
